import { makeValuesPredicate } from '@sgme/fp';
import classNames from 'classnames';
import { useSgBsTheme } from 'hooks/useSgBsTheme';
import type { IntlKey } from 'locales';
import type { BootstrapColor } from 'models/BootstrapVars';
import type { ReactNode } from 'react';
import { useIntl } from 'react-intl';

import { StatusContainerWithActionBar } from './styled';

const newColors = ['bid', 'ask'] as const;
type NewColor = (typeof newColors)[number];

type Color = BootstrapColor | NewColor | `lvl${1 | 2 | 3 | 4}`;

function getBackgroundColor(color: Color, isDiscreet = false) {
  return color.startsWith('lvl')
    ? `alert bg-${color}`
    : isDiscreet
      ? `alert alert-discreet-${color}`
      : `alert alert-${color}`;
}

export interface PanelHeaderProps {
  children?: ReactNode;
  color: Color;
  textColor?: BootstrapColor;
  discreet?: boolean;
  className?: string;
}

export function PanelHeader({ textColor, color, discreet, children, className }: PanelHeaderProps) {
  return (
    <StatusContainerWithActionBar
      className={classNames(
        `w-100 d-flex flex-column mb-0 rounded-top-3`,
        { [`text-${textColor}`]: textColor },
        getBackgroundColor(color, discreet),
        className,
      )}
    >
      {children}
    </StatusContainerWithActionBar>
  );
}

type PanelTitleProps = Readonly<{
  children: ReactNode;
  e2e?: string;
  className?: string;
}>;
export function PanelTitle({ children, e2e, className }: PanelTitleProps) {
  return (
    <div className={classNames(className, 'd-flex flex-column justify-content-center')}>
      <strong data-e2e={e2e}>{children}</strong>
    </div>
  );
}

export interface PanelFooterProps {
  children?: ReactNode;
  color: Color;
  textColor?: BootstrapColor;
  discreet?: boolean;
  icon: string;
  statusKey: IntlKey;
  className?: string;
  live?: boolean;
}

function NotReadyIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M10.8221 2C12.4341 2.1608 13.9018 2.80402 15.0887 3.77688L13.9499 4.92663C13.0596 4.23518 11.993 3.76884 10.8221 3.62412V2ZM18 9.19598H16.3799C16.2356 8.02211 15.7704 6.95276 15.0807 6.0603L16.2276 4.91859C17.198 6.10854 17.8396 7.5799 18 9.19598ZM16.2276 15.0894L15.0807 13.9397C15.7704 13.0472 16.2356 11.9779 16.3799 10.8121H18C17.8396 12.4201 17.198 13.8995 16.2276 15.0894ZM10.8221 18V16.3759C11.993 16.2312 13.0596 15.7648 13.9419 15.0734L15.0887 16.2231C13.9018 17.196 12.4341 17.8392 10.8221 18ZM9.21804 18V16.3759C6.05013 15.9819 3.60401 13.2804 3.60401 10C3.60401 6.7196 6.05013 4.01809 9.21804 3.62412V2C5.16792 2.40201 2 5.82714 2 10C2 14.1729 5.16792 17.598 9.21804 18Z"
        fill="#6E777A"
      />
      <g clipPath="url(#clip0_1529_14714)">
        <path
          d="M9.49816 14.5H8.99816L9.49816 11H7.74816C7.45816 11 7.46316 10.84 7.55816 10.67C7.65316 10.5 7.58316 10.63 7.59316 10.61C8.23816 9.47 9.20816 7.77 10.4982 5.5H10.9982L10.4982 9H12.2482C12.4932 9 12.5282 9.165 12.4832 9.255L12.4482 9.33C10.4782 12.775 9.49816 14.5 9.49816 14.5Z"
          fill="#6E777A"
        />
      </g>
      <defs>
        <clipPath id="clip0_1529_14714">
          <rect x="4" y="4" width="12" height="12" rx="6" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
}

const isBidAsk = makeValuesPredicate(['bid', 'ask']);

export function PanelFooter({
  children,
  textColor,
  color,
  icon,
  statusKey,
  discreet,
  className,
  live,
}: PanelFooterProps) {
  const { formatMessage } = useIntl();
  const isDiscreet = !isBidAsk(color);
  return (
    <div
      className={classNames(
        'd-flex align-items-center py-2 rounded-bottom-3',
        { [`text-${textColor}`]: textColor },
        getBackgroundColor(color, discreet),
        className,
      )}
    >
      {icon === null ? null : <em className="icon icon-sm me-2">{icon === 'not_ready' ? <NotReadyIcon /> : icon}</em>}
      <span className="flex-grow-1 text-nowrap" data-e2e="secondary-side-panel-status">
        {formatMessage({ id: statusKey })}
      </span>
      {children}
      {live === undefined ? null : (
        <span
          className={classNames('badge rounded-pill ms-2 d-flex align-items-center', {
            [`badge-${color}`]: live && !isDiscreet,
            'badge-primary': live && isDiscreet,
            'text-secondary': !live && isDiscreet,
            [`text-${color}`]: !live && !isDiscreet,
          })}
        >
          {live ? 'LIVE' : 'OFF'}
          <i className="icon icon-sm ms-2">{live ? 'sensors' : 'sensors_off'}</i>
        </span>
      )}
    </div>
  );
}

export function PendingPanelHeader({ children }: { children?: ReactNode }) {
  const theme = useSgBsTheme();
  return (
    <PanelHeader color="dark" textColor={theme === 'light' ? 'dark' : 'light'} discreet={theme === 'light'}>
      {children}
    </PanelHeader>
  );
}

export function PendingPanelFooter({ statusKey }: { statusKey: IntlKey }) {
  const theme = useSgBsTheme();
  return (
    <PanelFooter
      color="dark"
      textColor={theme === 'light' ? 'dark' : 'light'}
      statusKey={statusKey}
      discreet={theme === 'light'}
      icon="sync"
    />
  );
}

export function ErrorPanelHeader({ children }: { children: ReactNode }) {
  return (
    <PanelHeader color="danger" discreet className="align-items-start">
      {children}
    </PanelHeader>
  );
}

export function ErrorPanelFooter({ statusKey }: { statusKey: IntlKey }) {
  return <PanelFooter color="danger" statusKey={statusKey} icon="block" />;
}
